import React from 'react'
import { observer } from 'mobx-react'
import { observable } from 'mobx'
import { Form, Button, InputGroup, Modal, CloseButton } from 'react-bootstrap'
import { i18nTranslate } from 'src/utils'
import { IoIosClose as CloseIcon } from 'react-icons/io'
import { BsFillCheckCircleFill } from 'react-icons/bs'
import { accountsContainer, customerContainer } from 'src/models'
import { deleteFromSessionStorage } from 'config/appConfig'
import './styles.scss'

/**
 * @description - RenderForm return the form input element for the sponsor search field in this sponsor switch modal box
 * @date 10/12/2023 - 11:44:06 AM
 * @param { props } - onChange, removeSearch, placeholder, onClick, value.
 * @param { ref }
 * @return { JSX.Element }
 */
const RenderForm = React.forwardRef((props, ref) => {
  const {
    value = '',
    onFocus,
    onChange,
    placeHolder = '',
    removeSearch,
    onClick,
    onKeyDown,
  } = props
  let placeHolderText = ''

  placeHolderText = i18nTranslate('header_sponsorsearch', 'Sponsor Search', {
    nameSpace: 'ssr-resource',
  })

  const attributes = { value, onFocus, onChange, onClick, onKeyDown }
  return (
    <div className="position-relative flex-grow-1">
      <Form.Control
        ref={ref}
        type="text"
        placeholder={placeHolderText}
        {...attributes}
        aria-label={i18nTranslate('input.searchInput', 'search input textbox', {
          nameSpace: 'ssr-resource',
        })}
      />
    </div>
  )
})

RenderForm.displayName = 'RenderForm'

@observer
class SponsorSwitchModal extends React.Component {
  @observable sponsorName = ''
  @observable isValid = true
  @observable sponsorId = ''
  @observable searchValue = ''
  @observable lastValidatedSponsorId = ''

  async componentDidMount() {
    if (this.props?.sponsorFromLink) {
      this.searchValue = this.props?.sponsorFromLink || ''
      await this.handleSponsorSearch(this.searchValue)
    }
  }

  /**
   * @description handleSponsorSearch function triggers the sponsor search Api on blur / press on search button
   * @date 10/12/2023 - 10:50:07 AM
   *
   * @async
   * @param { String } value - the string value is from searchValue state variable
   * @returns { void }
   */
  handleSponsorSearch = async value => {
    const incoming = typeof value === 'string' ? value : this.searchValue
    const trimmed = (incoming || '').trim()
    if (!trimmed) {
      this.sponsorName = ''
      this.isValid = true
      this.lastValidatedSponsorId = ''
      return
    }
    const response = await customerContainer?.sponsorIdValidation(
      trimmed.toUpperCase()
    )
    if (customerContainer.isSuccessResponse(response)) {
      this.sponsorName = { ...response }
      this.isValid = true
      this.lastValidatedSponsorId = trimmed.toUpperCase()
    } else {
      this.sponsorName = ''
      this.isValid = false
      this.lastValidatedSponsorId = ''
    }
  }

  /**
   * @description - removeSearch function clears the input value in form when close button is clicked
   * @date 10/12/2023 - 11:00:10 AM
   */
  removeSearch = () => {
    this.searchValue = ''
    this.sponsorName = ''
    this.isValid = true
    this.lastValidatedSponsorId = ''
  }

  /**
   * @description - handleChange function sets the value from event to state variable
   * @date 10/12/2023 - 11:02:46 AM
   * @async
   * @param {*} event
   * @returns {void}
   */
  handleChange = async event => {
    let inputValue = event.target.value
    const sanitizedValue = inputValue?.replace(/[^a-zA-Z0-9]/g, '')
    this.searchValue = sanitizedValue
    if (!sanitizedValue) {
      this.sponsorName = ''
      this.isValid = true
      this.lastValidatedSponsorId = ''
      return
    }
    // If user modifies previously validated sponsor id (e.g., removes or changes any character), clear displayed sponsor immediately.
    if (
      this.sponsorName &&
      this.lastValidatedSponsorId &&
      sanitizedValue.toUpperCase() !== this.lastValidatedSponsorId
    ) {
      this.sponsorName = ''
      // Neutral state: don't show error until next explicit search
      this.isValid = true
      this.lastValidatedSponsorId = ''
    }
  }

  /**
   * @description handleKeyPress function is triggered when Enter key is pressed.
   * @date 10/17/2023 - 11:38:36 AM
   *
   * @async
   */
  handleKeyPress = async event => {
    event.preventDefault()
    await this.handleSponsorSearch(this.searchValue)
  }

  /**
   * @description handleSwitchSponsor function triggers the API for updating the sponsor in account and close the sponsor switch sponsor.
   * @date 10/12/2023 - 10:50:07 AM
   *
   * @async
   * @returns { void }
   */
  handleSwitchSponsor = async () => {
    const accountId = customerContainer?.accountId || ''
    const postData = {
      properties: [
        {
          attributeId: 'sponsorId',
          value: this.searchValue?.toUpperCase() || '',
        },
      ],
      accountId,
    }
    const response = await accountsContainer?.updateSponsorAccount(postData)
    this.removeSearch()
    this.props?.handleModal(true)
    deleteFromSessionStorage('switchsponsor')
  }

  render() {
    const searchText = i18nTranslate('header.globalSearch', 'Search', {
      nameSpace: 'ssr-resource',
    })
    const sponsorFirstName = this.sponsorName?.firstName || ''
    const sponsorLastName = this.sponsorName?.lastName || ''
    const sponsorId = this.sponsorName?.referenceAttribute1 || ''
    return (
      <Modal
        show={this.props?.showModal || false}
        className="sponsor-switch-modal-box-content">
        <Modal.Header>
          <h1 className="heading-2 sponsor-switch-modal-box-title">
            {i18nTranslate(
              'sponsorChange.sponsorSwitch',
              'Change my Sponsoring Affiliate'
            )}
          </h1>
          <CloseButton
            className="close"
            onClick={() => {
              this.removeSearch()
              this.props?.handleModal()
            }}
          />
        </Modal.Header>
        <Modal.Body className="align-item-center">
          <p className="sponsor-search-body-text">
            {i18nTranslate('sponsorChange.search', 'Search by Sponsor ID')}
          </p>
          <section
            className={`search-group d-flex flex-column position-relative flex-grow-1 ${
              this.isSuggestionVisible && 'search-suggestion-visible'
            }`}>
            <Form
              className={'nu-search-box sponsor-search-box'}
              onBlur={() =>
                this.searchValue && this.handleSponsorSearch(this.searchValue)
              }
              onSubmit={this.handleKeyPress}
              data-testid="qa-search">
              <InputGroup className="flex-nowrap flex-row-reverse">
                <div className="d-flex align-items-center">
                  {this.searchValue && (
                    <Button
                      size="sm"
                      variant="light"
                      onClick={this.removeSearch}
                      className={'nu-cancel-search'}>
                      <CloseIcon
                        size="1.5rem"
                        className="text-dark"
                        aria-label="clear input"
                      />
                    </Button>
                  )}
                </div>
                <RenderForm
                  value={this.searchValue}
                  onChange={this.handleChange}
                  placeHolder={searchText}
                  removeSearch={this.removeSearch}
                />
                <InputGroup.Append className="d-flex align-items-center search-icon-button border-0">
                  <Button
                    aria-label={i18nTranslate(
                      'searchBar.placeholder',
                      'Search',
                      {
                        nameSpace: 'ssr-resource',
                      }
                    )}
                    className="border-0"
                    onClick={() => this.handleSponsorSearch(this.searchValue)}>
                    <svg
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      className="search-box-svg"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.729736 9.98777C0.729736 4.47167 5.19572 0 10.7048 0C16.2139 0 20.6798 4.47167 20.6798 9.98777C20.6798 11.7568 20.2197 13.4205 19.4126 14.8629L23.9664 19.4844C24.9891 20.5223 24.9835 22.1921 23.9538 23.2231C22.9052 24.273 21.2001 24.2567 20.1718 23.1869L15.7613 18.5988C14.2782 19.4736 12.5489 19.9755 10.7048 19.9755C5.19572 19.9755 0.729736 15.5039 0.729736 9.98777ZM17.3685 17.42L21.5943 21.8159C21.8563 22.0884 22.2906 22.0926 22.5576 21.8251C22.8199 21.5625 22.8214 21.1372 22.5609 20.8728L18.2606 16.5087C17.9828 16.8311 17.6848 17.1355 17.3685 17.42ZM10.7048 1.97696C6.28617 1.97696 2.70418 5.56352 2.70418 9.98777C2.70418 14.412 6.28617 17.9986 10.7048 17.9986C12.4323 17.9986 14.0292 17.4515 15.3361 16.5208C16.1245 15.9594 16.8071 15.2583 17.3477 14.454C18.205 13.1784 18.7054 11.6431 18.7054 9.98777C18.7054 5.56352 15.1234 1.97696 10.7048 1.97696Z"
                        fill="#5F5F5F"
                      />
                    </svg>
                  </Button>
                </InputGroup.Append>
              </InputGroup>
            </Form>
            {typeof this.sponsorName?.firstName !== 'undefined' ||
            typeof this.sponsorName?.lastName !== 'undefined' ? (
              <div>
                <p className="sponsor-success font-weight-bold d-flex justify-content-between align-items-center">
                  <div className="sponsor-block">
                    <span className="sponsor-label">
                      {sponsorFirstName?.charAt(0)}
                      {sponsorLastName?.charAt(0)}
                    </span>
                    <div className="sponsor-namebox">
                      <ul>
                        <li className="sponsor-listname">
                          <span>
                            {sponsorFirstName} {sponsorLastName}
                          </span>
                        </li>
                        <li className="sponsor-listid">
                          <span>{sponsorId}</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <BsFillCheckCircleFill />
                </p>
              </div>
            ) : (
              !this.isValid && (
                <p className="text-danger sponsor-failure font-weight-bold">
                  {i18nTranslate(
                    'sponsorChange.incorrectId',
                    "We aren't able to find a sponsor with that ID"
                  )}
                </p>
              )
            )}
          </section>

          {this.isValid &&
            (typeof this.sponsorName?.firstName !== 'undefined' ||
              typeof this.sponsorName?.lastName !== 'undefined') && (
              <Button
                variant="outline-primary"
                className="sponsor-switch-button-modal"
                onClick={() => this.handleSwitchSponsor()}>
                {i18nTranslate('sponsorChange.switch', 'Change my Sponsor')}
              </Button>
            )}
        </Modal.Body>
      </Modal>
    )
  }
}

export default SponsorSwitchModal
export { SponsorSwitchModal }
